import classNames from "classnames";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import KuikaBadge from "../kuika-badge/kuika-badge";
import KuikaCheckbox from "../kuika-checkbox/kuika-checkbox";
import { KuikaNestedBadge } from "../kuika-nested-badge";
import KuikaSvg from "../kuika-svg/kuika-svg";
import KuikaTooltip from "../kuika-tooltip/kuika-tooltip";
import KuikaText from "../typography/Text/Text";
import KuikaTreeSelectFloating from "./kuika-tree-select-floating";
import "./kuika-tree-select.scss";

export interface ITreeSelectNode {
  id: string;
  name: string;
  path: string;
  type: "folder" | "leaf";
  children?: ITreeSelectNode[];
  /**
   * The inline mode never selects a disabled node. The floating mode never COMMITS one: a disabled
   * node with children still opens and closes, so a folder can be disabled and still be browsed.
   * The floating mode decides by `disabled` and `children` alone — `type` does not change what a
   * row does there.
   */
  disabled?: boolean;
  /** `data-testid` for the node's row (floating mode). */
  testId?: string;
}

/** What both modes take. */
export interface IKuikaTreeSelectBaseProps {
  /** Tree data structure */
  treeData: ITreeSelectNode[];
  /** Placeholder text when no selection */
  placeholder?: string;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Custom class name */
  className?: string;
  /** Maximum height of dropdown */
  maxHeight?: number;
}

/** The original mode: several leaf paths, ticked in a list that opens in the page's own flow. */
export interface IKuikaTreeSelectInlineProps extends IKuikaTreeSelectBaseProps {
  multiple?: true;
  popup?: "inline";
  /** Currently selected values (leaf paths) */
  value: string[];
  /** Callback when selection changes */
  onChange: (values: string[]) => void;
  /** Error message to display */
  errorMessage?: string;
  /** Whether to show checkboxes */
  showCheckbox?: boolean;
  /** Whether to show selected items as tags below */
  showTags?: boolean;
  /** Whether to show clear all button */
  showClearAll?: boolean;
  /** Label for the field (optional - can be rendered externally) */
  label?: string;
  /** Tooltip content for the label */
  labelTooltip?: React.ReactNode;
}

/**
 * One path, picked from a tree in a list that floats over the page. The list is portaled like
 * KuikaSelectbox's (document.body, or a KuikaPopupContainerContext provider's element), so it
 * works inside a scrolling panel. The field is KuikaSelectbox's field.
 */
export interface IKuikaTreeSelectSingleProps extends IKuikaTreeSelectBaseProps {
  multiple: false;
  popup: "floating";
  /** The picked node's `path`; `undefined` when nothing is picked. */
  value: string | undefined;
  /** Fires once per change: a pick of the value already held does not fire, a clear fires `undefined`. */
  onChange: (value: string | undefined) => void;
  /** A search input in the field. Matches a node's `name` or `path`, case-insensitively. */
  searchable?: boolean;
  allowClear?: boolean;
  /** Draw the tree's guide lines. */
  showLine?: boolean;
  /** Open every folder the first time the list is drawn. */
  defaultExpandAll?: boolean;
  size?: "sm" | "md" | "lg";
  /** Lands on the field's root — the element a click opens the list from. */
  dataTestId?: string;
  /** Where the list mounts. Defaults to the nearest KuikaPopupContainerContext, else document.body. */
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
}

export type KuikaTreeSelectProps = IKuikaTreeSelectInlineProps | IKuikaTreeSelectSingleProps;

/**
 * KuikaTreeSelectInline - A tree-based multi-select dropdown component
 *
 * Features:
 * - Collapsible folder structure
 * - Checkbox selection with indeterminate state for partial selection
 * - Animated dropdown
 * - Selected items displayed as removable tags
 * - Clear all functionality
 */
const KuikaTreeSelectInline: React.FC<IKuikaTreeSelectInlineProps> = ({
  treeData,
  value,
  onChange,
  placeholder = "Click to select...",
  disabled = false,
  errorMessage,
  showCheckbox = true,
  className,
  maxHeight = 256,
  showTags = true,
  showClearAll = true,
  label,
  labelTooltip
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen]);

  // Get all leaf paths from a node
  const getAllLeafPaths = useCallback((node: ITreeSelectNode): string[] => {
    if (node.type === "leaf") {
      return [node.path];
    }
    if (node.children) {
      return node.children.flatMap((child) => getAllLeafPaths(child));
    }
    return [];
  }, []);

  // Check if a node is fully selected
  const isNodeFullySelected = useCallback(
    (node: ITreeSelectNode): boolean => {
      const leafPaths = getAllLeafPaths(node);
      return leafPaths.length > 0 && leafPaths.every((path) => value.includes(path));
    },
    [value, getAllLeafPaths]
  );

  // Check if a node is partially selected
  const isNodePartiallySelected = useCallback(
    (node: ITreeSelectNode): boolean => {
      const leafPaths = getAllLeafPaths(node);
      const selectedCount = leafPaths.filter((path) => value.includes(path)).length;
      return selectedCount > 0 && selectedCount < leafPaths.length;
    },
    [value, getAllLeafPaths]
  );

  // Toggle node selection
  const toggleNodeSelection = useCallback(
    (node: ITreeSelectNode) => {
      if (node.type === "leaf") {
        if (value.includes(node.path)) {
          onChange(value.filter((v) => v !== node.path));
        } else {
          onChange([...value, node.path]);
        }
      } else {
        const leafPaths = getAllLeafPaths(node);
        const isFullySelected = isNodeFullySelected(node);

        if (isFullySelected) {
          onChange(value.filter((v) => !leafPaths.includes(v)));
        } else {
          const newValues = [...value];
          leafPaths.forEach((path) => {
            if (!newValues.includes(path)) {
              newValues.push(path);
            }
          });
          onChange(newValues);
        }
      }
    },
    [value, onChange, getAllLeafPaths, isNodeFullySelected]
  );

  // Toggle expanded state
  const toggleExpanded = useCallback((nodeId: string) => {
    setExpandedKeys((prev) => (prev.includes(nodeId) ? prev.filter((k) => k !== nodeId) : [...prev, nodeId]));
  }, []);

  // Remove a single value
  const handleRemoveValue = useCallback(
    (path: string) => {
      onChange(value.filter((v) => v !== path));
    },
    [value, onChange]
  );

  // Clear all values
  const handleClearAll = useCallback(() => {
    onChange([]);
  }, [onChange]);

  // Toggle dropdown
  const handleToggleDropdown = useCallback(() => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  }, [disabled]);

  // Keyboard support for the trigger: Enter / Space / ArrowDown open the dropdown, Escape closes
  // it and keeps focus on the trigger.
  const handleTriggerKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      if (disabled) {
        return;
      }
      if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
        event.preventDefault();
        setIsOpen(true);
      } else if (event.key === "Escape") {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    },
    [disabled]
  );

  // Render a tree node
  const renderNode = useCallback(
    (node: ITreeSelectNode, level: number): React.ReactNode => {
      const isExpanded = expandedKeys.includes(node.id);
      const isSelected = node.type === "leaf" ? value.includes(node.path) : isNodeFullySelected(node);
      const isIndeterminate = node.type === "folder" && isNodePartiallySelected(node);

      return (
        <div key={node.id} style={{ paddingLeft: level * 12 }}>
          <div
            className={classNames("kuika-tree-select__node-row", {
              "kuika-tree-select__node-row--disabled": node.disabled
            })}
            aria-disabled={node.disabled || undefined}
          >
            {/* Expand/Collapse button for folders */}
            {node.type === "folder" ? (
              <div
                className="kuika-tree-select__node-expand"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpanded(node.id);
                }}
              >
                <KuikaSvg name={isExpanded ? "minus" : "plus"} type="common" width={10} height={10} />
              </div>
            ) : (
              <div className="kuika-tree-select__node-spacer" />
            )}

            {/* Checkbox */}
            {showCheckbox && (
              <KuikaCheckbox
                className="kuika-tree-select__node-checkbox"
                checked={isSelected}
                indeterminate={isIndeterminate}
                disabled={node.disabled}
                ariaLabel={node.name}
                dataTestId={`treeSelectCheckbox-${node.id}`}
                onChange={() => {
                  if (!node.disabled) {
                    toggleNodeSelection(node);
                  }
                }}
              />
            )}

            {/* Node label */}
            <div
              className={classNames("kuika-tree-select__node-label", {
                "kuika-tree-select__node-label--disabled": node.disabled
              })}
              onClick={(e) => {
                e.stopPropagation();
                if (node.type === "folder") {
                  toggleExpanded(node.id);
                } else if (!showCheckbox && !node.disabled) {
                  toggleNodeSelection(node);
                }
              }}
            >
              {node.type === "folder" && <KuikaSvg name="folder" type="sidebar-icons" width={14} height={14} />}
              <KuikaText className="kuika-tree-select__node-label-text">{node.name}</KuikaText>
            </div>
          </div>

          {/* Children */}
          {node.type === "folder" && isExpanded && node.children && (
            <div className="kuika-tree-select__node-children">
              {node.children.map((child) => renderNode(child, level + 1))}
            </div>
          )}
        </div>
      );
    },
    [
      expandedKeys,
      value,
      showCheckbox,
      isNodeFullySelected,
      isNodePartiallySelected,
      toggleExpanded,
      toggleNodeSelection
    ]
  );

  // Get all paths from tree in their original order
  const getAllPathsInOrder = useCallback((nodes: ITreeSelectNode[]): string[] => {
    const paths: string[] = [];
    const traverse = (node: ITreeSelectNode) => {
      if (node.type === "leaf") {
        paths.push(node.path);
      }
      if (node.children) {
        node.children.forEach(traverse);
      }
    };
    nodes.forEach(traverse);
    return paths;
  }, []);

  // Group selected values by parent object, maintaining tree order
  const groupedValues = useMemo(() => {
    // Get all paths in their original tree order
    const orderedPaths = getAllPathsInOrder(treeData);

    // Filter to only selected values, maintaining tree order
    const orderedSelectedPaths = orderedPaths.filter((path) => value.includes(path));

    // Group by parent while maintaining order
    const groups: { [key: string]: string[] } = {};
    const groupOrder: string[] = []; // Track order of groups as they appear

    orderedSelectedPaths.forEach((path) => {
      const parts = path.split(".");
      if (parts.length > 1) {
        // Has parent object
        const parentKey = parts[0];
        if (!groups[parentKey]) {
          groups[parentKey] = [];
          groupOrder.push(parentKey);
        }
        groups[parentKey].push(path);
      } else {
        // Top-level field (no parent)
        if (!groups._root) {
          groups._root = [];
          if (!groupOrder.includes("_root")) {
            groupOrder.push("_root");
          }
        }
        groups._root.push(path);
      }
    });

    return { groups, groupOrder };
  }, [value, treeData, getAllPathsInOrder]);

  // Remove all values from a group
  const handleRemoveGroup = useCallback(
    (groupKey: string) => {
      const pathsToRemove = groupedValues.groups[groupKey] || [];
      onChange(value.filter((v) => !pathsToRemove.includes(v)));
    },
    [value, onChange, groupedValues]
  );

  // Display text for trigger
  const triggerText = useMemo(() => {
    if (value.length === 0) {
      return placeholder;
    }
    return `${value.length} field(s) selected`;
  }, [value, placeholder]);

  return (
    <div className={classNames("kuika-tree-select", className)} ref={containerRef}>
      {/* Header with label and clear all */}
      {(label || (showClearAll && value.length > 0)) && (
        <div className="kuika-tree-select__header">
          {label && (
            <span className="k_Regular-G1-Left kuika_rightPanelDIName" style={{ margin: 0 }}>
              {label}
              {labelTooltip && (
                <KuikaTooltip content={labelTooltip}>
                  <span className="kuika-tree-select__label-info">
                    <KuikaSvg name="info-circle" width={14} height={14} color="var(--dark-5)" />
                  </span>
                </KuikaTooltip>
              )}
            </span>
          )}
          {showClearAll && value.length > 0 && (
            <span className="kuika-tree-select__clear-btn" onClick={handleClearAll}>
              Clear all
            </span>
          )}
        </div>
      )}

      {/* Trigger */}
      <div
        ref={triggerRef}
        className={classNames("kuika-tree-select__trigger", {
          "kuika-tree-select__trigger--open": isOpen,
          "kuika-tree-select__trigger--disabled": disabled
        })}
        role="combobox"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={isOpen}
        aria-haspopup="tree"
        aria-disabled={disabled || undefined}
        onClick={handleToggleDropdown}
        onKeyDown={handleTriggerKeyDown}
      >
        <span
          className={classNames("kuika-tree-select__trigger-text", {
            "kuika-tree-select__trigger-text--placeholder": value.length === 0
          })}
        >
          {triggerText}
        </span>
        <div
          className={classNames("kuika-tree-select__trigger-icon", {
            "kuika-tree-select__trigger-icon--open": isOpen
          })}
        >
          <KuikaSvg name="chevron-down" type="common" width={12} height={12} color="var(--dark-5)" />
        </div>
      </div>

      {/* Dropdown */}
      <div
        className={classNames("kuika-tree-select__dropdown", {
          "kuika-tree-select__dropdown--open": isOpen
        })}
        style={{ maxHeight: isOpen ? maxHeight : 0 }}
      >
        {treeData.length === 0 ? (
          <span className="kuika-tree-select__empty">No options available</span>
        ) : (
          treeData.map((node) => renderNode(node, 0))
        )}
      </div>

      {/* Selected tags grouped by object */}
      {showTags && value.length > 0 && (
        <div className="kuika-tree-select__tags">
          {groupedValues.groupOrder.map((groupKey) => {
            const paths = groupedValues.groups[groupKey];
            // Check if this is a group or single top-level fields
            const isGroup = groupKey !== "_root" && paths.length > 0;
            const isRootGroup = groupKey === "_root";

            if (isGroup) {
              // Render as a nested badge with group containing children
              const childLabels = paths.map((path) => path.split(".").slice(1).join("."));
              return (
                <KuikaNestedBadge
                  key={groupKey}
                  groupLabel={groupKey}
                  children={childLabels}
                  groupColor="blue"
                  childColor="blue"
                  onRemoveGroup={() => handleRemoveGroup(groupKey)}
                  onRemoveChild={(childLabel) => {
                    // Find the full path for this child label
                    const fullPath = paths.find((path) => path.split(".").slice(1).join(".") === childLabel);
                    if (fullPath) {
                      handleRemoveValue(fullPath);
                    }
                  }}
                />
              );
            }
            if (isRootGroup) {
              // Render top-level fields without grouping
              return paths.map((path) => (
                <KuikaBadge
                  key={path}
                  content={path}
                  color="blue"
                  rightIcon={
                    <button
                      type="button"
                      className="kuika-tree-select__tag-close"
                      aria-label={`Remove ${path}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveValue(path);
                      }}
                    >
                      <KuikaSvg name="x" width={12} height={12} />
                    </button>
                  }
                />
              ));
            }
            return null;
          })}
        </div>
      )}

      {/* Error message */}
      {errorMessage && <span className="kuika-tree-select__error">{errorMessage}</span>}
    </div>
  );
};

/**
 * KuikaTreeSelect — pick from a tree. `popup` picks the mode: the default is the inline multi-select
 * (`value: string[]`), `popup="floating"` with `multiple={false}` the single pick in a floating list.
 */
const KuikaTreeSelect: React.FC<KuikaTreeSelectProps> = (props: KuikaTreeSelectProps): JSX.Element => {
  if (props.popup === "floating") {
    return <KuikaTreeSelectFloating {...props} />;
  }
  return <KuikaTreeSelectInline {...props} />;
};

export default KuikaTreeSelect;
